import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { MouseEvent as MausklickImFenster, ReactNode } from 'react'
import { t } from './sprache'

/** Wo das Fenster steht: seine linke obere Ecke, in Bildpunkten vom Bildrand. */
type Stelle = { x: number; y: number }

/**
 * **Der Kopf bleibt im Bild** (R711). Nach unten darf das Fenster hinausragen
 * — das ist der Sinn des Verschiebens: eine lange Maske wegschieben und
 * darüber nachlesen, was dahinter steht. Nach oben, links und rechts nicht:
 * Am Kopf hängen Titel, Kreuz und der Griff selbst, und ein Kopf, der halb
 * hinausragt, nimmt den Weg zurück.
 */
function imBild(x: number, y: number, fenster: HTMLElement, kopfhoehe: number): Stelle {
  return {
    x: Math.max(0, Math.min(x, window.innerWidth - fenster.offsetWidth)),
    y: Math.max(0, Math.min(y, window.innerHeight - kopfhoehe)),
  }
}

/**
 * **Das Fenster, das an mehreren Stellen dasselbe tut** (R515, R710).
 *
 * Es gibt in CoKo bereits zwei kleine Fenster — die Nachfrage und die
 * Texteingabe (R394). Die tragen genau einen Satz und genau eine Tat; sie
 * brauchen weder Größe noch Rand. Dieses hier ist das andere: **ein Fenster
 * für eine Maske**, die man liest, in der man wählt und die je nach Element
 * kurz oder lang ist. Deshalb bringt es mit, was eine Maske braucht:
 *
 * - **Ein Kreuz oben rechts**, weil ein Fenster ohne sichtbaren Ausgang
 *   aussieht wie eine Sackgasse.
 * - **Esc schließt**, solange der Fokus im Fenster steht. Nicht am ganzen
 *   Bildschirm: Ein Esc, das aus jedem Winkel der Seite greift, schlösse auch
 *   das, was jemand gerade im Hintergrund aufgeklappt hat.
 * - **Ein Schalter im Kopf macht groß und wieder klein**, und der
 *   Doppelklick auf den Kopf tut dasselbe. Eine Liste mit vierzig
 *   Fundstellen will die ganze Fläche, dieselbe Maske mit zwei Zeilen will
 *   sie nicht — und welcher Fall vorliegt, sieht der Anwender, nicht das
 *   Programm.
 * - **Die Ecke lässt sich ziehen** und zeigt, daß sie es kann. Das ist die
 *   feine Einstellung zwischen den beiden Größen; gezogen wird mit einer
 *   CSS-Zeile (`resize`) statt mit einer eigenen Ziehmechanik, die Marke in
 *   der Ecke liegt nur darüber.
 * - **Der Kopf ist der Griff.** Ziehen verschiebt das Fenster.
 *
 * **Warum beides sichtbar im Kopf steht** (Befund vom 2026-09-29: *„Das
 * Fenster für die IC-Abstimmung ist statisch"*). Größe ändern konnte das
 * Fenster von Anfang an — nur hing das Großmachen an einem Doppelklick auf
 * den Kopf und das Ziehen an einer Ecke ohne Marke. Ein Handgriff, den man
 * nicht sieht, hat für den Anwender nicht stattgefunden; deshalb trägt der
 * Kopf jetzt einen Schalter neben dem Kreuz, und die Ecke eine Marke.
 *
 * **Warum ein breites Fenster bildschirmfüllend aufgeht** (7.4 Abschnitt 29.4a,
 * R834 — Auftrag vom 2026-09-29). Wer eine Maske mit dreizehn Spalten in einem Fenster von
 * 1100 Punkten aufschlägt, zieht erst einmal auf — jedesmal denselben
 * Handgriff, bevor die Arbeit beginnt. Und weil die gezogene Größe
 * **nicht** über das Schließen hinaus gemerkt wird, stehen Reiter und
 * Knopfreihe bei jedem Öffnen an derselben Stelle. Die Weite entscheidet:
 * `breit` geht groß auf, `schmal` und `mittel` nicht — eine Liste mit
 * fünf Zeilen auf dem ganzen Bildschirm wäre viel Fläche für nichts.
 *
 * **Warum das Fenster an einer festen Stelle steht und nicht in einer
 * gerechneten Mitte** (R711). Ein Rahmen, der das Fenster fortwährend
 * mittig rückt, arbeitet beim Ziehen gegen die Hand: Wird das Fenster 100
 * Punkte breiter, rutscht es zugleich 50 Punkte nach links, und die Ecke
 * unter der Hand kommt nur halb so weit. Deshalb ist die Stelle eine
 * Eigenschaft des Fensters — es geht mittig auf und steht danach still. Die
 * obere linke Ecke rührt sich nicht mehr, die untere rechte folgt der Hand
 * eins zu eins, und dieselbe feste Stelle ist das, woran ein Verschieben
 * überhaupt angreifen kann.
 *
 * **Warum die gemerkte Größe im Verweis steht und nicht im Zustand.** Das
 * Ziehen an der Ecke schreibt Breite und Höhe in das Element selbst; eine
 * Klassenregel käme dagegen nicht an. Beim Großmachen wird das Geschriebene
 * deshalb beiseitegelegt und beim Verkleinern zurückgeholt — so behält das
 * Fenster die Größe, die jemand ihm gegeben hat.
 */
export function Dialogfenster({
  titel,
  kopfzusatz,
  fuss,
  weite = 'mittel',
  aufSchliessen,
  children,
}: {
  titel: string
  /** Was neben der Überschrift steht — eine Auskunft, kein zweiter Titel. */
  kopfzusatz?: ReactNode
  /**
   * Die Knopfreihe am Fuß. Sie steht **fest** und scrollt nicht mit: In einem
   * Fenster, das man kleiner ziehen kann, wäre ein mitlaufender Knopf genau
   * dann außer Sicht, wenn man ihn sucht.
   */
  fuss?: ReactNode
  /** Wie breit das Fenster anfängt — geändert wird es danach mit der Hand. */
  weite?: 'schmal' | 'mittel' | 'breit'
  aufSchliessen: () => void
  children: ReactNode
}) {
  const rahmen = useRef<HTMLDivElement>(null)
  const kopf = useRef<HTMLElement>(null)
  const gemerkteGroesse = useRef<{ breite: string; hoehe: string } | null>(null)
  const [gross, setGross] = useState(weite === 'breit')
  const [stelle, setStelle] = useState<Stelle | null>(null)
  /** Der Abstand zwischen Mauszeiger und linker oberer Ecke, solange geschoben wird. */
  const [zug, setZug] = useState<{ dx: number; dy: number } | null>(null)
  const kennung = useId()

  /*
    Der Fokus steht beim Öffnen im Fenster (R143) — sonst hörte Esc auf das,
    was dahinter liegt, und die Tabulatortaste liefe durch die Liste im
    Hintergrund.
  */
  useEffect(() => {
    rahmen.current?.focus()
  }, [])

  /*
    **Das Fenster geht mittig auf** und steht danach still (R711). Die Mitte
    wird einmal gerechnet, nicht fortwährend — und sie wird hier gerechnet und
    nicht im Stilblatt, weil sie die Größe des Fensters braucht, die erst nach
    dem Zeichnen feststeht. Vor dem Zeichnen heißt: vor dem Bild, sonst sähe
    man das Fenster einen Wimpernschlag lang in der Ecke.

    **Gerechnet wird beim ersten Mal, in dem das Fenster klein ist** — das ist
    beim bildschirmfüllenden Aufgehen nicht der erste Durchgang, sondern der
    erste Klick auf den Schalter. Im großen Zustand wäre die Mitte der Rand,
    und das Fenster käme beim Verkleinern in der Ecke zurück. Das `?? ` hält
    danach jede verschobene Stelle fest: Gerechnet wird genau einmal.
  */
  useLayoutEffect(() => {
    const el = rahmen.current
    if (!el || gross) {
      return
    }

    setStelle(
      (alt) =>
        alt ??
        imBild(
          Math.round((window.innerWidth - el.offsetWidth) / 2),
          Math.round((window.innerHeight - el.offsetHeight) / 2),
          el,
          kopf.current?.offsetHeight ?? 0,
        ),
    )
  }, [gross])

  /*
    **Wird das Browserfenster kleiner, rückt das Fenster nach** — so weit, dass
    der Kopf wieder ganz im Bild steht, und keinen Punkt weiter. Das ist die
    eine Bewegung, die das Fenster von allein macht; ohne sie ließe ein
    kleinergezogener Browser die Knopfreihe unerreichbar zurück.
  */
  useEffect(() => {
    function nachruecken() {
      const el = rahmen.current
      if (!el) {
        return
      }

      setStelle((alt) => (alt ? imBild(alt.x, alt.y, el, kopf.current?.offsetHeight ?? 0) : alt))
    }

    window.addEventListener('resize', nachruecken)
    return () => window.removeEventListener('resize', nachruecken)
  }, [])

  /*
    **Geschoben wird am Fenster, gehorcht wird dem ganzen Bildschirm.** Wer
    schnell zieht, ist mit dem Zeiger längst neben dem Kopf — hörte nur der
    Kopf zu, bliebe das Fenster unterwegs stehen. Deshalb hängen Bewegung und
    Loslassen am Fenster des Browsers und nicht an der Griffleiste.
  */
  useEffect(() => {
    if (!zug) {
      return
    }

    const { dx, dy } = zug

    function fuehren(e: MouseEvent) {
      const el = rahmen.current
      if (!el) {
        return
      }

      setStelle(imBild(e.clientX - dx, e.clientY - dy, el, kopf.current?.offsetHeight ?? 0))
    }

    function beenden() {
      setZug(null)
    }

    window.addEventListener('mousemove', fuehren)
    window.addEventListener('mouseup', beenden)
    return () => {
      window.removeEventListener('mousemove', fuehren)
      window.removeEventListener('mouseup', beenden)
    }
  }, [zug])

  /**
   * **Der Kopf ist der Griff — bis auf das Kreuz** (R711). Wer den Ausgang
   * anfasst, will hinaus und nicht schieben. Und im großen Zustand wird gar
   * nicht geschoben: Groß heißt die ganze Fläche, ein Griff hätte dort keinen
   * Weg mehr (R143).
   */
  function zugBeginnen(e: MausklickImFenster<HTMLElement>) {
    const el = rahmen.current
    if (!el || gross || e.button !== 0 || (e.target as HTMLElement).closest('button')) {
      return
    }

    const ecke = el.getBoundingClientRect()
    setZug({ dx: e.clientX - ecke.left, dy: e.clientY - ecke.top })
  }

  /**
   * **Gefragt wird der Zustand, nicht die gemerkte Größe** (Auftrag vom
   * 2026-09-29). Solange jedes Fenster klein aufging, war „es liegt eine
   * gemerkte Größe vor" dasselbe wie „es ist groß". Ein Fenster, das
   * bildschirmfüllend aufgeht, hat beim ersten Klick nichts gemerkt — und
   * wäre an der alten Weiche ein zweites Mal groß geworden. Ohne gemerkte
   * Größe fällt es auf die Breite seiner Weite zurück, und das ist genau die
   * Größe, mit der es früher aufgegangen wäre.
   */
  function groesseWechseln() {
    const el = rahmen.current
    if (!el) {
      return
    }

    if (gross) {
      if (gemerkteGroesse.current) {
        el.style.width = gemerkteGroesse.current.breite
        el.style.height = gemerkteGroesse.current.hoehe
        gemerkteGroesse.current = null
      }
      setGross(false)
    } else {
      gemerkteGroesse.current = { breite: el.style.width, hoehe: el.style.height }
      el.style.width = ''
      el.style.height = ''
      setGross(true)
    }
  }

  return (
    /*
      Der Klick daneben schließt — aber nur, wenn er daneben *anfängt*: Wer im
      Fenster markiert und mit der Maus darüber hinausfährt, hat nicht
      abgebrochen.
    */
    <div
      className="dialoghof"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          aufSchliessen()
        }
      }}
    >
      <div
        ref={rahmen}
        className={`dialogfenster ${weite} ${gross ? 'gross' : ''}`.trim()}
        /*
          Im großen Zustand steht hier nichts: Eine Angabe am Element schlüge
          jede Klassenregel, und die gemerkte Stelle käme beim Verkleinern
          nicht zurück.
        */
        style={gross || !stelle ? undefined : { left: stelle.x, top: stelle.y }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={kennung}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation()
            aufSchliessen()
          }
        }}
      >
        <header
          ref={kopf}
          onMouseDown={zugBeginnen}
          onDoubleClick={groesseWechseln}
          title={t('fenster.griff')}
        >
          <h2 id={kennung}>{titel}</h2>
          {/*
            **Der Zusatz steht nur da, wo er etwas anderes sagt als der Titel**
            (Befund vom 2026-09-27): Im Fenster eines Prozesses stand links
            "Kapitalkonsolidierung" und rechts noch einmal dasselbe -- ein
            Prozess heisst ab Werk wie sein Werkzeug. Geprueft wird hier und
            nicht an den Aufrufstellen, damit es das naechste Fenster nicht
            wieder selbst bedenken muss.
          */}
          {kopfzusatz && kopfzusatz !== titel && (
            <span className="kopfzusatz">{kopfzusatz}</span>
          )}
          {/*
            **Der Schalter für die Größe steht neben dem Ausgang** (Befund vom
            2026-09-29). Er trägt kein Wort, sondern das Bild seines Ziels: das
            Rechteck bis an den Rand heißt bildschirmfüllend, das kleinere
            Rechteck heißt Fenster. Was er tut, steht im Hinweis und für die
            Sprachausgabe an der Schaltfläche — ein Zeichen allein wäre für
            jeden, der vorliest, keine Auskunft (R143).
          */}
          <button
            type="button"
            className="fenstergroesse"
            aria-label={t(gross ? 'fenster.alsfenster' : 'fenster.bildschirmfuellend')}
            title={t(gross ? 'fenster.alsfenster' : 'fenster.bildschirmfuellend')}
            onClick={groesseWechseln}
          >
            <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              {gross ? (
                <rect x="3.5" y="4.5" width="9" height="7" rx="1" />
              ) : (
                <rect x="1.5" y="2.5" width="13" height="11" rx="1" />
              )}
            </svg>
          </button>

          <button
            type="button"
            className="schliesskreuz"
            aria-label={t('fenster.schliessen')}
            title={t('fenster.schliessen')}
            onClick={aufSchliessen}
          >
            ×
          </button>
        </header>

        <div className="dialoginhalt">{children}</div>

        {fuss && <footer className="dialogfuss">{fuss}</footer>}

        {/*
          **Die Ecke zeigt, daß sie ziehbar ist.** Gezogen wird weiterhin am
          Fenster selbst (`resize`); diese Marke liegt nur darüber und läßt
          jeden Klick durch — ein eigenes Bedienteil hier hätte die
          Ziehmechanik nachbauen müssen. Im großen Zustand steht sie nicht da:
          Dort gibt es nichts zu ziehen.
        */}
        {!gross && <span className="ziehecke" aria-hidden="true" />}
      </div>
    </div>
  )
}
